import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Pause,
  RefreshCw,
  ScanLine,
  ShieldAlert,
  TriangleAlert,
  UserCheck,
  UserPlus,
} from 'lucide-react';
import { formatDuration } from '@/lib/i18n';
import type { ResourceSessionEventPayload } from '@/lib/shared-types';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { reconnectRealtime } from '@/lib/realtime';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { StationModeToggle, useStationMode } from '@/components/StationMode';
import { KidsCheckInDialog, KidsCheckOutDialog } from './components/KidsAreaDialogs';
import { KidsWaitingList } from './components/KidsWaitingList';
import { useChildDirectory } from './components/kids-area';
import { useMeterTick, type SessionRow } from './lib/sessions';
import { compareOccupants, occupantOf, urgencyOf, type Occupant } from './kidsDesk';
import {
  WAITING_LIST_POLL_MS,
  invalidateKidsArea,
  useKidsAreaSessionsQuery,
  useKidsAreasQuery,
  useKidsOccupancyQuery,
  useKidsWaitingListQuery,
} from './kidsStationApi';
import { useKidsStationLive } from './kidsStationLive';

/** The area this terminal works. A desk belongs to one, the way a KDS does. */
const AREA_KEY = 'venue.kids.resourceId';

/** Where station mode's own flag lives, one per screen. */
const STATION_KEY = 'venue.kids.focus';

/**
 * §11 — the kids-area desk, as a screen rather than as a dialog on the floor map.
 *
 * ## What is on it, and what is deliberately not
 *
 * The attendant is usually mid-conversation with a parent, so the board answers
 * the four questions that get asked out loud and nothing else:
 *
 *  1. **Who is in the area, and how long have they been?** One card per child,
 *     ordered by who has to be dealt with next — over their booked time first,
 *     then nearest to it. Dwell is wall-clock, not billable time: a paused timer
 *     bills nothing and the child is still in the room.
 *  2. **Who is waiting?** The queue, seatable and cancellable in place.
 *  3. **Check a child in.**
 *  4. **Give a child back.** The one action that must be impossible to get
 *     wrong, so it opens the existing check-out dialog with its named-guardian
 *     rule and its manager override intact (`kidsDesk.ts` holds the verdict;
 *     `kidsDesk.test.ts` and `KidsStation.collection.test.tsx` hold it still).
 *
 * Not here, on purpose: pricing rules, the floor map, entitlement previews, and
 * anything that would invite the desk to record more about a child than the
 * venue already holds. The model stores a display name, a band, and the adults
 * to call — no age, no photo, no note — and a field on this screen is how that
 * stops being true.
 *
 * ## The safe action and the unsafe one
 *
 * Both live on the same card, and they are not the same size. **Check out** is
 * the primary control; releasing a child to anyone but the guardian on file is
 * not a variant of it but a second, red, separately-worded act behind a manager
 * PIN and a written reason, drawn only once the desk has actually named someone
 * else. A child with no guardian recorded is flagged amber on the board *before*
 * anyone tries to collect them, because that handover will need a manager and
 * the time to find one is now, not when the parent is at the counter.
 */
export default function KidsStation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermission();
  const dispatch = useAppDispatch();
  const activeBranchId = useAppSelector((state) => state.ui.activeBranchId);
  const { code: currency } = useCurrency();
  const stationMode = useStationMode(STATION_KEY);

  const canViewSessions = can('resource_sessions.view');
  const canViewResources = can('resources.view');
  const canViewChildren = can('children.view');
  // Both halves, because the API demands both: a check-in registers a child and
  // links a guardian to them, so it is `children.manage` as well as the timer
  // key. A person who may not read who a child's mother is may not sign the
  // child out to her either.
  const canCheckIn = can('resource_sessions.start') && can('children.manage');
  const canCheckOut = can('resource_sessions.stop') && can('children.manage');

  const [resourceId, setResourceId] = useState(() => localStorage.getItem(AREA_KEY) ?? '');
  const [checkingIn, setCheckingIn] = useState(false);
  const [collecting, setCollecting] = useState<SessionRow | null>(null);

  useEffect(() => {
    if (resourceId) localStorage.setItem(AREA_KEY, resourceId);
    else localStorage.removeItem(AREA_KEY);
  }, [resourceId]);

  // ------------------------------------------------------------------ reads

  const areas = useKidsAreasQuery(undefined, { skip: !canViewResources });
  const areaRows = useMemo(() => areas.data ?? [], [areas.data]);
  const resource = areaRows.find((row) => row.id === resourceId) ?? null;

  const activeArgs = useMemo(() => ({ resourceId, status: 'ACTIVE' as const }), [resourceId]);
  const pausedArgs = useMemo(() => ({ resourceId, status: 'PAUSED' as const }), [resourceId]);
  const ready = !!resourceId && canViewSessions;

  /**
   * `refetchOnMountOrArgChange` on all three: a cached board is worth painting
   * the instant the desk opens, and worth trusting only for as long as the
   * revalidation takes. A kids area is the last screen that should render a
   * confident five-minute-old answer to "who is in the room".
   */
  const active = useKidsAreaSessionsQuery(activeArgs, {
    skip: !ready,
    refetchOnMountOrArgChange: true,
  });
  const paused = useKidsAreaSessionsQuery(pausedArgs, {
    skip: !ready,
    refetchOnMountOrArgChange: true,
  });
  const occupancy = useKidsOccupancyQuery(resourceId, {
    skip: !ready,
    refetchOnMountOrArgChange: true,
  });
  // The queue is a fold over `AuditLog` and emits nothing — see the note on
  // `WAITING_LIST_POLL_MS`. This is the only poll on the screen.
  const queue = useKidsWaitingListQuery(resourceId, {
    skip: !ready,
    refetchOnMountOrArgChange: true,
    pollingInterval: WAITING_LIST_POLL_MS,
  });

  const sessions = useMemo(
    () => [...(active.data ?? []), ...(paused.data ?? [])],
    [active.data, paused.data],
  );

  /**
   * Who is actually in the room, and who may take them home.
   *
   * `GET /resource-sessions` hands this screen a `childId` and stops: a child's
   * name, band and guardians sit behind `children.view`, which the floor roles
   * do not hold, so the shape a waiter reads on the floor map cannot carry them.
   * The desk holds the key and asks — one read per child, and a kids area holds
   * a handful.
   */
  const childIds = useMemo(
    () => sessions.map((session) => session.childId ?? '').filter(Boolean),
    [sessions],
  );
  const children = useChildDirectory(childIds);

  const now = useMeterTick(sessions.length > 0);
  const occupants = useMemo(
    () =>
      sessions
        .map((session) => occupantOf(session, children[session.childId ?? ''] ?? null, now))
        .sort(compareOccupants),
    [sessions, children, now],
  );

  const reload = useCallback(() => {
    if (!resourceId) return;
    dispatch(invalidateKidsArea(resourceId));
  }, [dispatch, resourceId]);

  // --------------------------------------------------------------- realtime

  /** So the expiry alert can name the child rather than the room. */
  const occupantsRef = useRef<Occupant[]>(occupants);
  occupantsRef.current = occupants;

  const onExpiring = useCallback(
    (payload: ResourceSessionEventPayload) => {
      const child = occupantsRef.current.find(
        (occupant) => occupant.session.id === payload.resourceSessionId,
      );
      toast.warning(
        t('spaces.resources.expiring', {
          name: child?.childName ?? resource?.name ?? '',
          count: Math.max(0, Math.round((payload.remainingSeconds ?? 0) / 60)),
        }),
      );
    },
    [t, resource?.name],
  );

  const live = useKidsStationLive({
    resourceId: resourceId || null,
    branchId: activeBranchId ?? null,
    onSessionEvent: reload,
    onExpiring,
    onResync: reload,
  });

  // ----------------------------------------------------------------- render

  const missing = [
    ...(canViewResources ? [] : ['resources.view']),
    ...(canViewSessions ? [] : ['resource_sessions.view']),
    ...(canViewChildren ? [] : ['children.view']),
  ];

  if (missing.length) {
    // Not a blank screen and not a 403 page: the attendant is standing at a
    // desk and needs to know which key to ask a manager for.
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">
          {t('spaces.kids.stationTitle')}
        </h1>
        <EmptyState
          title={t('spaces.kids.stationDenied')}
          hint={t('spaces.kids.stationDeniedHint', {
            keys: missing.join(', '),
          })}
        />
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">
          {t('spaces.kids.stationTitle')}
        </h1>
        <p className="text-sm text-ink-muted">
          {t('spaces.kids.pickArea')}
        </p>

        {areas.error ? (
          <ErrorState
            message={
              (areas.error as { message?: string }).message ??
              t('spaces.resources.actionFailed')
            }
            onRetry={() => void areas.refetch()}
          />
        ) : areas.isLoading ? (
          <LoadingState label={t('common.loading')} />
        ) : !areaRows.length ? (
          <EmptyState
            title={t('spaces.kids.noAreas')}
            /*
             * The hint has to name the actual place, and it used to name the
             * wrong one: nothing in Settings creates a kids area, so the only
             * instruction on this screen sent the operator somewhere that could
             * not help them. Resources are created in exactly one place — the
             * resource board behind Floor map's Resources button.
             *
             * It also has to say *resource*. What this desk lists is a resource
             * of type KIDS_AREA, not an area named "Kids area" — those are
             * different records, and the area is the more obvious thing to
             * reach for, so a venue can build one, come back here, and have no
             * way to tell why the desk still says there is nothing.
             *
             * And it says no tables are needed, because that is the other thing
             * an operator reasonably assumes and then spends time on: a kids
             * area holds children, not covers.
             */
            hint={t('spaces.kids.noAreasHint')}
            action={
              can('resources.create') ? (
                <Button onClick={() => navigate('/floor-map')}>
                  {t('spaces.kids.goToFloorMap')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 [&>*]:min-w-0">
            {areaRows.map((area) => (
              <button
                key={area.id}
                onClick={() => setResourceId(area.id)}
                className="rounded-xl border bg-surface p-5 text-start shadow-sm transition-colors hover:bg-hover"
              >
                <div className="text-base font-semibold">{area.name}</div>
                <div className="text-xs text-ink-muted">
                  {t('spaces.resources.capacity', { count: area.capacity })}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const counts = occupancy.data;
  const free = counts?.free ?? Math.max(0, resource.capacity - occupants.length);
  const fetching =
    active.isFetching || paused.isFetching || occupancy.isFetching || queue.isFetching;
  const loading = active.isLoading && paused.isLoading;
  const boardError = active.error ?? paused.error;
  const stale = !live.connected || !!live.authFailure;

  return (
    <div ref={stationMode.ref} className={stationMode.shellClassName}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={resourceId}
            onChange={(event) => setResourceId(event.currentTarget.value)}
            className="h-11 w-56 text-base font-semibold"
            aria-label={t('spaces.kids.pickArea')}
          >
            {areaRows.map((area) => (
              <option key={area.id} value={area.id}>
                {area.name}
              </option>
            ))}
          </Select>
          <span className="text-sm text-ink-muted">
            {t('spaces.resources.occupancy', {
              used: counts?.occupied ?? occupants.length,
              capacity: counts?.capacity ?? resource.capacity,
            })}
            {' · '}
            {t('spaces.kids.freePlaces', { count: free })}
          </span>
          <Badge tone={stale ? 'warning' : 'success'}>
            {stale ? t('kds.board.reconnecting') : t('kds.board.live')}
          </Badge>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {fetching && !loading && (
            <span className="text-xs text-ink-muted">{t('operations.common.updating')}</span>
          )}
          <Button variant="outline" size="sm" onClick={reload} disabled={fetching}>
            <RefreshCw className="h-4 w-4" />
            {t('operations.common.refresh')}
          </Button>
          <StationModeToggle mode={stationMode} />
          <Button size="sm" onClick={() => setCheckingIn(true)} disabled={!canCheckIn}>
            <UserPlus className="h-4 w-4" />
            {t('spaces.resources.checkIn')}
          </Button>
        </div>
      </div>

      {/*
        A stalled board and an empty play area look identical, and one of them
        means a child is unaccounted for. Say which it is.
      */}
      {stale && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-400 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40"
        >
          <span className="flex min-w-0 items-center gap-2 font-semibold text-amber-900 dark:text-amber-200">
            <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 break-words">
              {t('spaces.kids.staleWarning')}
            </span>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              reconnectRealtime();
              reload();
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-3 [&>*]:min-w-0">
        <section className="space-y-3 xl:col-span-2">
          <h2 className="text-sm font-semibold text-ink-muted">
            {t('spaces.kids.inArea')}
          </h2>

          {boardError ? (
            <ErrorState
              message={
                (boardError as { message?: string }).message ??
                t('spaces.resources.actionFailed')
              }
              onRetry={reload}
            />
          ) : loading ? (
            <LoadingState label={t('common.loading')} />
          ) : !occupants.length ? (
            <EmptyState
              title={t('spaces.kids.nobodyIn')}
              hint={t('spaces.kids.nobodyInHint')}
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3 [&>*]:min-w-0">
              {occupants.map((occupant) => (
                <OccupantCard
                  key={occupant.session.id}
                  occupant={occupant}
                  canCollect={canCheckOut}
                  onCollect={() => setCollecting(occupant.session)}
                />
              ))}
            </div>
          )}
        </section>

        <aside>
          <Card>
            <CardHeader>
              <CardTitle>{t('spaces.kids.queueTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <KidsWaitingList
                resourceId={resource.id}
                tickets={queue.data ?? []}
                loading={queue.isLoading}
                free={free}
                onChanged={reload}
              />
            </CardContent>
          </Card>
        </aside>
      </div>

      <KidsCheckInDialog
        open={checkingIn}
        resource={resource}
        currency={currency}
        onClose={() => setCheckingIn(false)}
        onDone={reload}
      />
      <KidsCheckOutDialog
        open={!!collecting}
        resource={resource}
        sessions={sessions}
        session={collecting}
        currency={currency}
        onClose={() => setCollecting(null)}
        onDone={reload}
      />
    </div>
  );
}

const URGENCY_BORDER = {
  over: 'border-red-400 dark:border-red-900',
  soon: 'border-amber-400 dark:border-amber-900',
  ok: '',
} as const;

/**
 * One child, as the desk needs to see them from arm's length: the name big
 * enough to read across a counter, the band beside it because that is what a
 * parent quotes, the guardian because that is who may take them, and the two
 * clocks — how long they have been here, and how long is left.
 */
function OccupantCard({
  occupant,
  canCollect,
  onCollect,
}: {
  occupant: Occupant;
  canCollect: boolean;
  onCollect: () => void;
}) {
  const { t } = useTranslation();
  const urgency = urgencyOf(occupant);

  return (
    <Card className={URGENCY_BORDER[urgency]}>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold">
              {occupant.childName ?? t('spaces.resources.guest')}
            </p>
            {occupant.tagCode && (
              <p className="flex items-center gap-1 text-xs text-ink-muted" dir="ltr">
                <ScanLine className="h-3 w-3 shrink-0" aria-hidden />
                {occupant.tagCode}
              </p>
            )}
          </div>
          {occupant.paused && (
            <Badge tone="neutral">
              <Pause className="me-1 h-3 w-3" aria-hidden />
              {t('spaces.resources.status.PAUSED')}
            </Badge>
          )}
        </div>

        {/*
          Named guardian or not, said before anyone asks to collect. A child with
          no guardian on file can only be released with a manager's signature,
          and the desk should know that while there is still time to find one.
        */}
        {occupant.guardianIds.length > 0 ? (
          <p className="truncate text-sm">
            {t('spaces.kids.withGuardian', {
              name: (occupant.child?.guardians ?? [])
                .map((link) => link.guardian.name)
                .join(', '),
            })}
          </p>
        ) : (
          <p className="flex items-center gap-1 text-sm text-amber-700 dark:text-amber-400">
            <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
            {t('spaces.kids.noGuardian')}
          </p>
        )}

        <dl className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <dt className="text-xs text-ink-muted">
              {t('spaces.kids.dwell')}
            </dt>
            <dd className="tabular-nums" dir="ltr">
              {formatDuration(occupant.dwellSeconds)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">{t('resource.remaining')}</dt>
            <dd
              className={
                urgency === 'over'
                  ? 'font-semibold tabular-nums text-red-700 dark:text-red-400'
                  : urgency === 'soon'
                    ? 'font-semibold tabular-nums text-amber-700 dark:text-amber-400'
                    : 'tabular-nums'
              }
              dir="ltr"
            >
              {occupant.remainingSeconds === null
                ? '—'
                : urgency === 'over'
                  ? t('kds.board.overBy', { time: formatDuration(occupant.overdueSeconds) })
                  : t('kds.board.dueIn', { time: formatDuration(occupant.remainingSeconds) })}
            </dd>
          </div>
        </dl>

        <Button className="w-full" onClick={onCollect} disabled={!canCollect}>
          <UserCheck className="h-4 w-4" />
          {t('spaces.resources.checkOut')}
        </Button>
      </CardContent>
    </Card>
  );
}
